import React, { useEffect, useMemo, useRef, useCallback } from "react";
import get from "lodash/get";
import { useFormContext, Controller } from "react-hook-form";
import { Box, TextField, Typography } from "@material-ui/core";
import { MeterRegister } from "../../../api/meters";
import { useValidationLabel } from "../useValidationLabel";
import { isTabDirty } from "../utils/isTabDirty";
import { NumberField } from "../../../components/NumberField";

type Reading = {
  id: string;
  cumulative: string;
};

export interface RegisterProps {
  autofocus: boolean;
  index: number;
  fieldNamePrefix: string;
  label: string;
  meterId: string;
  activeTabId: string;
  config: MeterRegister;
}

export const Register: React.FC<RegisterProps> = ({
  index,
  fieldNamePrefix,
  autofocus,
  meterId,
  activeTabId,
  label,
  config,
  ...props
}) => {
  const {
    register,
    errors,
    formState: { dirtyFields },
  } = useFormContext();

  const [idName, cumulativeName] = useMemo(
    () => [
      `${fieldNamePrefix}.readings[${index}].id`,
      `${fieldNamePrefix}.readings[${index}].cumulative`,
    ],
    [fieldNamePrefix, index],
  );
  const error = get(errors, [fieldNamePrefix, "readings", index, "cumulative"]);
  const helperText = useMemo(
    () => error?.message || `Please enter up to ${config.digits} digits`,
    [config.digits, error],
  );

  const required = useRef<boolean>(false);
  required.current =
    isTabDirty(meterId, dirtyFields) || meterId === activeTabId;

  // Support for field name => display label matching
  useValidationLabel(cumulativeName, label);

  // Support for autofocus on mount
  const readingFieldRef = useRef<HTMLInputElement>();
  /* istanbul ignore next: the effort to mock these browser APIs isn't worth it */
  const focus = useCallback(() => {
    if (readingFieldRef.current) {
      readingFieldRef.current.focus();
      readingFieldRef.current.setSelectionRange(0, 0);
    }
  }, [readingFieldRef]);

  /* istanbul ignore next */
  useEffect(() => {
    if (autofocus) {
      focus();
    }
  }, [focus, autofocus]);

  return (
    <Box data-testid="Register" mt={4} {...props}>
      <input ref={register} type="hidden" name={idName} value={config.id} />
      <Box width={[1, 1 / 2, 1 / 3]} minWidth={[null, 300]}>
        <Controller
          name={cumulativeName}
          defaultValue=""
          onFocus={focus}
          rules={{
            validate: (value) =>
              !required.current || !!value ? true : "This field is required",
          }}
          as={
            <TextField
              data-testid="Register__Field--reading"
              label={label}
              variant="outlined"
              fullWidth
              InputProps={{
                inputComponent: NumberField as any,
                endAdornment: (
                  <Box pl={2}>
                    <Typography style={{ fontSize: 17 }}>
                      {config.unit}
                    </Typography>
                  </Box>
                ),
              }}
              inputProps={{
                digits: config.digits,
                decimalPlaces: config.decimalPlaces,
                autoComplete: "off",
                style: { textAlign: "right", letterSpacing: 5 },
              }}
              inputRef={readingFieldRef}
              error={!!error}
              helperText={helperText}
              required
            />
          }
        />
      </Box>
    </Box>
  );
};
